/**
 * Progress Tracking Service
 *
 * Centralized service for all progress-related Firebase operations.
 * Handles progress tracking, mastery updates, review scheduling, and badge awarding.
 */

import { ref as dbRef, get, set } from 'firebase/database'
import { db } from '@/lib/firebase'
import {
  LessonProgress,
  LessonMastery,
  ReviewSchedule,
  Badge,
  Streak,
  Lesson,
  Enrollment,
} from '@/types/models'
import {
  updateLessonMastery,
  createInitialReviewSchedule,
  updateReviewSchedule,
  filterDueReviews,
} from './masteryService'
import {
  checkForNewBadges,
  createBadge,
  updateStreak,
  createInitialStreak,
} from './achievementService'
import { updateStreak as updateStreakData } from './streakService'

/**
 * User progress data aggregated from multiple Firebase paths
 */
export interface UserProgressData {
  progress: Record<string, LessonProgress>
  mastery: Record<string, LessonMastery>
  reviews: ReviewSchedule[]
  badges: Badge[]
  streak: Streak | null
  enrollments: Record<string, Enrollment>
}

/**
 * Fetch all progress-related data for a user
 */
export async function fetchUserProgressData(uid: string): Promise<UserProgressData> {
  const [
    progressSnapshot,
    masterySnapshot,
    reviewsSnapshot,
    badgesSnapshot,
    streakSnapshot,
    enrollmentsSnapshot,
  ] = await Promise.all([
    get(dbRef(db, `progress/${uid}`)),
    get(dbRef(db, `mastery/${uid}`)),
    get(dbRef(db, `reviews/${uid}`)),
    get(dbRef(db, `badges/${uid}`)),
    get(dbRef(db, `streaks/${uid}`)),
    get(dbRef(db, `enrollments/${uid}`)),
  ])

  return {
    progress: progressSnapshot.exists() ? progressSnapshot.val() : {},
    mastery: masterySnapshot.exists() ? masterySnapshot.val() : {},
    reviews: reviewsSnapshot.exists()
      ? (Object.values(reviewsSnapshot.val()) as ReviewSchedule[])
      : [],
    badges: badgesSnapshot.exists() ? (Object.values(badgesSnapshot.val()) as Badge[]) : [],
    streak: streakSnapshot.exists() ? streakSnapshot.val() : null,
    enrollments: enrollmentsSnapshot.exists() ? enrollmentsSnapshot.val() : {},
  }
}

/**
 * Initialize or update user streak when they visit the app
 */
export async function updateUserStreak(uid: string): Promise<Streak> {
  const streakRef = dbRef(db, `streaks/${uid}`)
  const snapshot = await get(streakRef)

  let streak: Streak

  if (snapshot.exists()) {
    const currentStreak = snapshot.val() as Streak
    streak = updateStreak(currentStreak, Date.now())
    if (streak !== currentStreak) {
      await set(streakRef, streak)
    }
  } else {
    streak = createInitialStreak()
    await set(streakRef, streak)
  }

  return streak
}

/**
 * Get lesson progress for a specific lesson
 */
export async function getLessonProgress(
  uid: string,
  lessonId: string
): Promise<LessonProgress | null> {
  const progressRef = dbRef(db, `progress/${uid}/${lessonId}`)
  const snapshot = await get(progressRef)
  return snapshot.exists() ? snapshot.val() : null
}

/**
 * Initialize progress for a lesson (when user first opens it)
 */
export async function initializeLessonProgress(
  uid: string,
  lessonId: string
): Promise<LessonProgress> {
  const existingProgress = await getLessonProgress(uid, lessonId)
  if (existingProgress) {
    return existingProgress
  }

  const newProgress: LessonProgress = {
    lessonId,
    status: 'in_progress',
    attempts: 0,
    updatedAt: Date.now(),
  }

  const progressRef = dbRef(db, `progress/${uid}/${lessonId}`)
  await set(progressRef, newProgress)

  return newProgress
}

/**
 * Record a quiz attempt and update all related data
 * This is the main function that orchestrates progress tracking
 */
export async function recordQuizAttempt(
  uid: string,
  lessonId: string,
  score: number,
  passed: boolean
): Promise<{
  progress: LessonProgress
  mastery: LessonMastery
  reviewSchedule?: ReviewSchedule
  newBadges: Badge[]
  attemptNumber: number
  latestScore: number
  bestScore: number
}> {
  const now = Date.now()

  // 1. Get existing progress
  const existingProgress = await getLessonProgress(uid, lessonId)

  // 2. Calculate attempt number
  const attemptNumber = (existingProgress?.attempts || 0) + 1
  const bestScore = Math.max(score, existingProgress?.score || 0)

  // 3. Update lesson progress
  // Build object without undefined values (Firebase rejects undefined)
  const updatedProgress: LessonProgress = {
    lessonId,
    status: passed ? 'completed' : 'in_progress',
    score: bestScore, // Keep best score
    attempts: attemptNumber,
    updatedAt: now,
  }

  // Only set completedAt if we have a value
  const completedAt = passed ? now : existingProgress?.completedAt
  if (completedAt !== undefined) {
    updatedProgress.completedAt = completedAt
  }

  const progressRef = dbRef(db, `progress/${uid}/${lessonId}`)
  await set(progressRef, updatedProgress)

  // 4. Store the attempt with metadata
  const attemptRef = dbRef(db, `attempts/${uid}/${lessonId}/${now}`)
  await set(attemptRef, {
    score,
    timestamp: now,
    attemptNumber,
    passed
  })

  // 5. Get all attempts for mastery calculation
  const attemptsSnapshot = await get(dbRef(db, `attempts/${uid}/${lessonId}`))
  const allAttempts: { score: number; timestamp: number }[] = attemptsSnapshot.exists()
    ? Object.values(attemptsSnapshot.val())
    : []

  // 6. Calculate and store mastery
  const mastery = updateLessonMastery(updatedProgress, allAttempts, uid)
  const masteryRef = dbRef(db, `mastery/${uid}/${lessonId}`)
  await set(masteryRef, mastery)

  // 7. Handle spaced repetition schedule if passed
  let reviewSchedule: ReviewSchedule | undefined
  if (passed) {
    const reviewRef = dbRef(db, `reviews/${uid}/${lessonId}`)
    const reviewSnapshot = await get(reviewRef)

    if (reviewSnapshot.exists()) {
      const existingSchedule = reviewSnapshot.val() as ReviewSchedule
      reviewSchedule = updateReviewSchedule(existingSchedule, mastery.masteryScore)
    } else {
      reviewSchedule = createInitialReviewSchedule(lessonId, uid, mastery.masteryScore)
    }

    await set(reviewRef, reviewSchedule)
  }

  // 8. Update daily streak
  await updateStreakData(uid)

  // 9. Check and award new badges
  const newBadges = await checkAndAwardBadges(uid)

  return {
    progress: updatedProgress,
    mastery,
    reviewSchedule,
    newBadges,
    attemptNumber,
    latestScore: score,
    bestScore,
  }
}

/**
 * Check prerequisites for a lesson (mastery gating)
 */
export async function checkPrerequisites(
  uid: string,
  lesson: Lesson
): Promise<{
  canAccess: boolean
  missingPrerequisites: string[]
  prerequisites: Array<{
    lessonId: string
    completed: boolean
    masteryLevel?: string
    masteryScore?: number
  }>
}> {
  if (!lesson.prerequisiteLessonIds || lesson.prerequisiteLessonIds.length === 0) {
    return { canAccess: true, missingPrerequisites: [], prerequisites: [] }
  }

  const progressData = await fetchUserProgressData(uid)
  const prerequisites: Array<{
    lessonId: string
    completed: boolean
    masteryLevel?: string
    masteryScore?: number
  }> = []
  const missingPrerequisites: string[] = []

  for (const prereqId of lesson.prerequisiteLessonIds) {
    const progress = progressData.progress[prereqId]
    const mastery = progressData.mastery[prereqId]
    const completed = progress?.status === 'completed'

    prerequisites.push({
      lessonId: prereqId,
      completed,
      masteryLevel: mastery?.masteryLevel,
      masteryScore: mastery?.masteryScore,
    })

    if (!completed) {
      missingPrerequisites.push(prereqId)
    }
  }

  return {
    canAccess: missingPrerequisites.length === 0,
    missingPrerequisites,
    prerequisites,
  }
}

/**
 * Get reviews that are due for a user
 */
export async function getDueReviews(uid: string): Promise<ReviewSchedule[]> {
  const reviewsSnapshot = await get(dbRef(db, `reviews/${uid}`))
  if (!reviewsSnapshot.exists()) {
    return []
  }

  const allReviews = Object.values(reviewsSnapshot.val()) as ReviewSchedule[]
  return filterDueReviews(allReviews)
}

/**
 * Complete a review and update the schedule
 */
export async function completeReview(
  uid: string,
  lessonId: string,
  score: number
): Promise<ReviewSchedule> {
  // This uses the same flow as quiz completion
  const result = await recordQuizAttempt(uid, lessonId, score, score >= 80)

  if (!result.reviewSchedule) {
    // Should not happen if lesson was previously completed, but handle gracefully
    const mastery = result.mastery
    const reviewSchedule = createInitialReviewSchedule(lessonId, uid, mastery.masteryScore)
    const reviewRef = dbRef(db, `reviews/${uid}/${lessonId}`)
    await set(reviewRef, reviewSchedule)
    return reviewSchedule
  }

  return result.reviewSchedule
}

/**
 * Check for and award new badges based on current stats
 */
export async function checkAndAwardBadges(uid: string): Promise<Badge[]> {
  const [progressSnapshot, badgesSnapshot, masterySnapshot, streakSnapshot, enrollmentsSnapshot] = await Promise.all([
    get(dbRef(db, `progress/${uid}`)),
    get(dbRef(db, `badges/${uid}`)),
    get(dbRef(db, `mastery/${uid}`)),
    get(dbRef(db, `streaks/${uid}`)),
    get(dbRef(db, `enrollments/${uid}`)),
  ])

  const progressData = progressSnapshot.exists() ? progressSnapshot.val() : {}
  const existingBadges = badgesSnapshot.exists()
    ? (Object.values(badgesSnapshot.val()) as Badge[])
    : []
  const masteryData = masterySnapshot.exists() ? masterySnapshot.val() : {}
  const streakData = streakSnapshot.exists() ? streakSnapshot.val() : null
  const enrollmentsData = enrollmentsSnapshot.exists() ? enrollmentsSnapshot.val() : {}

  const completedLessons = Object.values(progressData).filter(
    (p: any) => p.status === 'completed'
  ).length

  const hasPerfectScore = Object.values(progressData).some((p: any) => p.score === 100)

  const masterLevelCount = Object.values(masteryData).filter(
    (m: any) => m.masteryLevel === 'master'
  ).length

  const coursesCompleted = Object.values(enrollmentsData).filter(
    (e: any) => e.status === 'completed'
  ).length

  const stats = {
    lessonsCompleted: completedLessons,
    coursesCompleted,
    hasPerfectScore,
    currentStreak: streakData?.currentStreak || 0,
    masterLevelCount,
  }

  const newBadgeTypes = checkForNewBadges(uid, stats, existingBadges)
  const newBadges: Badge[] = []

  for (const badgeType of newBadgeTypes) {
    const badge = createBadge(badgeType, uid)
    // Use badge TYPE as key to prevent duplicates (not unique ID)
    const badgeRef = dbRef(db, `badges/${uid}/${badgeType}`)
    await set(badgeRef, badge)
    newBadges.push(badge)
  }

  return newBadges
}

/**
 * Mark audio as listened for a lesson
 * This prevents auto-play on subsequent visits
 */
export async function markAudioListened(
  uid: string,
  lessonId: string
): Promise<void> {
  const progressRef = dbRef(db, `progress/${uid}/${lessonId}`)
  const snapshot = await get(progressRef)

  if (snapshot.exists()) {
    const existing = snapshot.val() as LessonProgress
    await set(progressRef, {
      ...existing,
      audioListened: true,
      updatedAt: Date.now(),
    })
  } else {
    // Initialize progress if it doesn't exist
    const newProgress: LessonProgress = {
      lessonId,
      status: 'in_progress',
      attempts: 0,
      audioListened: true,
      updatedAt: Date.now(),
    }
    await set(progressRef, newProgress)
  }
}

/**
 * Unmark audio as listened for a lesson
 * Allows the audio to auto-play again on next visit
 */
export async function unmarkAudioListened(
  uid: string,
  lessonId: string
): Promise<void> {
  const progressRef = dbRef(db, `progress/${uid}/${lessonId}`)
  const snapshot = await get(progressRef)

  if (snapshot.exists()) {
    const existing = snapshot.val() as LessonProgress
    await set(progressRef, {
      ...existing,
      audioListened: false,
      updatedAt: Date.now(),
    })
  }
}

/**
 * Get user statistics for display
 */
export async function getUserStats(uid: string): Promise<{
  lessonsCompleted: number
  totalLessons: number
  averageMastery: number
  currentStreak: number
  longestStreak: number
  totalActiveDays: number
  badgeCount: number
  masterLevelCount: number
  reviewsDue: number
}> {
  const [progressData, lessonsSnapshot] = await Promise.all([
    fetchUserProgressData(uid),
    get(dbRef(db, 'lessons')),
  ])

  const totalLessons = lessonsSnapshot.exists() ? Object.keys(lessonsSnapshot.val()).length : 0

  const lessonsCompleted = Object.values(progressData.progress).filter(
    p => p.status === 'completed'
  ).length

  const masteryScores = Object.values(progressData.mastery).map(m => m.masteryScore)
  const averageMastery =
    masteryScores.length > 0
      ? Math.round(masteryScores.reduce((a, b) => a + b, 0) / masteryScores.length)
      : 0

  const masterLevelCount = Object.values(progressData.mastery).filter(
    m => m.masteryLevel === 'master'
  ).length

  const dueReviews = filterDueReviews(progressData.reviews)

  return {
    lessonsCompleted,
    totalLessons,
    averageMastery,
    currentStreak: progressData.streak?.currentStreak || 0,
    longestStreak: progressData.streak?.longestStreak || 0,
    totalActiveDays: progressData.streak?.totalActiveDays || 0,
    badgeCount: progressData.badges.length,
    masterLevelCount,
    reviewsDue: dueReviews.length,
  }
}

/**
 * Update course enrollment status based on lesson completion
 * Marks enrollment as 'completed' when all course lessons are finished
 */
export async function updateEnrollmentProgress(
  uid: string,
  courseId: string,
  lessonIds: string[]
): Promise<Enrollment> {
  const progressSnapshot = await get(dbRef(db, `progress/${uid}`))
  const progressData = progressSnapshot.exists() ? progressSnapshot.val() : {}

  const completedInCourse = lessonIds.filter(id => progressData[id]?.status === 'completed').length
  const allLessonsCompleted = completedInCourse === lessonIds.length

  const enrollmentRef = dbRef(db, `enrollments/${uid}/${courseId}`)
  const enrollmentSnapshot = await get(enrollmentRef)

  const now = Date.now()
  let enrollment: Enrollment

  if (enrollmentSnapshot.exists()) {
    const existing = enrollmentSnapshot.val() as Enrollment
    enrollment = {
      ...existing,
      status: allLessonsCompleted ? 'completed' : 'active',
    }
  } else {
    // Create enrollment if it doesn't exist
    enrollment = {
      courseId,
      userId: uid,
      enrolledAt: now,
      status: allLessonsCompleted ? 'completed' : 'active',
      progressPercentage: 0,
      lastAccessedAt: now,
    }
  }

  await set(enrollmentRef, enrollment)
  return enrollment
}
